"use client"

import * as React from "react"
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuTrigger } from "@/components/ui/context-menu"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { AppIcon } from "@/components/desk/AppIcon"
import { WidgetIcon } from "@/components/widgets/WidgetIcon"
import { Calendar } from "@/components/ui/calendar"
import { DesktopCalendarWidget } from "@/components/widgets/CalendarApp"
import { Button } from "@/components/ui/button"
import { Search, Plus, Pencil, X, Bell, Settings, CloudSun, MapPin, ChevronLeft, ChevronRight, Calendar as CalendarIcon, Notebook, Calculator, Pin, PinOff, LayoutGrid, ChevronsUpDown, Check, Briefcase } from "lucide-react"
import { PinnedWidgetsPanel, PanelSide } from "@/components/desk/PinnedWidgetsPanel"

interface DesktopAppGridProps {
  isMobile: boolean
  mobileWallpaperStyle: React.CSSProperties
  desktopWallpaperStyle: React.CSSProperties
  openApps: any[]
  notifications: any[]
  currentTime: string
  weatherInfo: string
  mobileSearch: string
  setMobileSearch: (s: string) => void
  editMode: boolean
  setEditMode: (m: boolean) => void
  showToast: (title: string, desc: string, variant?: any) => void
  setShowWidgetLibraryModal: (show: boolean) => void
  installedApps: any[]
  finalMobileHomeOrder: string[]
  handleHomeDragStart: (e: React.DragEvent, key: string) => void
  handleHomeDragOver: (e: React.DragEvent) => void
  handleHomeDrop: (e: React.DragEvent, key: string) => void
  handleOpenWidgetAsApp: (wid: string, name: string, icon: string) => void
  widgetsConfig: any[]
  setWidgetsConfig: React.Dispatch<React.SetStateAction<any[]>>
  moveHomeItem: (key: string, dir: "left" | "right") => void
  notesContent: string
  setNotesContent: (s: string) => void
  calcFormula: string
  calcDisplay: string
  handleCalcPress: (val: string) => void
  handleOpenApp: (app: any) => void
  setHiddenAppIds: React.Dispatch<React.SetStateAction<string[]>>
  hiddenAppIds: string[]
  renamedAppNames: Record<string, string>
  theme: string | undefined
  setShowMobileNotifications: (s: boolean) => void
  setShowMobileControlCenter: (s: boolean) => void
  setShowMobileAppSwitcher: (s: boolean) => void
  handleDesktopContextMenu: (e: React.MouseEvent) => void
  handleDesktopDrop: (e: React.DragEvent) => void
  setContextMenu: React.Dispatch<React.SetStateAction<any>>
  windowSize: { width: number; height: number }
  getCellSize: () => number
  getDesktopIconConfig: () => any
  appPositions: Record<string, { col: number; row: number }>
  draggedAppId: string | null
  handleAppIconDragStart: (e: React.DragEvent, appId: string) => void
  setOpenApps: React.Dispatch<React.SetStateAction<any[]>>
  setSplitActiveAppId: (id: string | null) => void
  setFocusedPane: (pane: "left" | "right") => void
  handleRenameAppClick?: (id: string, name?: string) => void
  collapseWidgets?: boolean
  setCollapseWidgets?: (val: boolean) => void
  widgetPanelSide?: PanelSide
  setWidgetPanelSide?: (side: PanelSide) => void
  calendarDate?: Date
  setCalendarDate?: (date?: Date) => void
  pinnedWidgets?: string[]
  handlePinWidget?: (id: string) => void
  pinnedWidgetSides?: Record<string, PanelSide>
  setPinnedWidgetSides?: React.Dispatch<React.SetStateAction<Record<string, PanelSide>>>
  collapsedSides?: Record<PanelSide, boolean>
  setCollapsedSides?: React.Dispatch<React.SetStateAction<Record<PanelSide, boolean>>>
  sideWidths?: Record<"left" | "right", number>
  setSideWidths?: React.Dispatch<React.SetStateAction<Record<"left" | "right", number>>>
  topPanelHeight?: number
  setTopPanelHeight?: React.Dispatch<React.SetStateAction<number>>
  minimizedWidgets?: Set<string>
  calcFocusedWidgetId?: string | null
  setCalcFocusedWidgetId?: (id: string | null) => void
  handleMinimizeWidget?: (id: string) => void
  handleToggleFloatingWidget?: (id: string) => void
  showCalcHistory?: boolean
  setShowCalcHistory?: React.Dispatch<React.SetStateAction<boolean>>
  calcHistory?: any[]
  setCalcHistory?: React.Dispatch<React.SetStateAction<any[]>>
  setCalcResetOnNext?: React.Dispatch<React.SetStateAction<boolean>>
  setCalcDisplay?: React.Dispatch<React.SetStateAction<string>>
  setCalcFormula?: React.Dispatch<React.SetStateAction<string>>
  setPinnedWidgets?: React.Dispatch<React.SetStateAction<string[]>>
  mobileDockAppIds?: string[]
  setMobileDockAppIds?: React.Dispatch<React.SetStateAction<string[]>>
  desktopGridCols?: number
  desktopIconSize?: number
  desktopShowLabels?: boolean
  topBarConfig?: any
  currentAccount?: any
  currentWorkspace?: any
  setCurrentWorkspace?: (ws: any) => void
  handleSwitchWorkspace?: (ws: any) => void
}

export function DesktopAppGrid({
  isMobile,
  mobileWallpaperStyle,
  desktopWallpaperStyle,
  openApps,
  notifications,
  currentTime,
  weatherInfo,
  mobileSearch,
  setMobileSearch,
  editMode,
  setEditMode,
  showToast,
  setShowWidgetLibraryModal,
  installedApps,
  finalMobileHomeOrder,
  handleHomeDragStart,
  handleHomeDragOver,
  handleHomeDrop,
  handleOpenWidgetAsApp,
  widgetsConfig,
  setWidgetsConfig,
  moveHomeItem,
  notesContent,
  setNotesContent,
  calcFormula,
  calcDisplay,
  handleCalcPress,
  handleOpenApp,
  setHiddenAppIds,
  hiddenAppIds,
  renamedAppNames,
  theme,
  setShowMobileNotifications,
  setShowMobileControlCenter,
  setShowMobileAppSwitcher,
  handleDesktopContextMenu,
  handleDesktopDrop,
  setContextMenu,
  windowSize,
  getCellSize,
  getDesktopIconConfig,
  appPositions,
  draggedAppId,
  handleAppIconDragStart,
  setOpenApps,
  setSplitActiveAppId,
  setFocusedPane,
  handleRenameAppClick,
  collapseWidgets,
  setCollapseWidgets,
  widgetPanelSide,
  setWidgetPanelSide,
  calendarDate,
  setCalendarDate,
  pinnedWidgets = [],
  handlePinWidget,
  setPinnedWidgets,
  mobileDockAppIds = [],
  setMobileDockAppIds,
  desktopGridCols = 12,
  desktopIconSize = 48,
  desktopShowLabels = true,
  topBarConfig,
  currentAccount,
  currentWorkspace,
  setCurrentWorkspace,
  handleSwitchWorkspace,
}: DesktopAppGridProps) {
  const availableWorkspaces = React.useMemo(() => {
    if (!currentAccount?.workspaces) {
      if (currentWorkspace) return [currentWorkspace]
      return []
    }
    return currentAccount.workspaces.filter((w: any) => w.status !== false)
  }, [currentAccount, currentWorkspace])

  const availableWidgets = React.useMemo(() => {
    const standard = [
      { id: "system-weather", name: "Clima", icon: "🌤️", iconComp: CloudSun, desc: "Previsão do tempo" },
      { id: "system-calendar", name: "Calendário", icon: "📅", iconComp: CalendarIcon, desc: "Visualizar datas e eventos" },
      { id: "system-notes", name: "Notas", icon: "📝", iconComp: Notebook, desc: "Anotações rápidas" },
      { id: "system-calculator", name: "Calculadora", icon: "🔢", iconComp: Calculator, desc: "Calculadora do sistema" },
    ]
    const standardIds = new Set(standard.map((s) => s.id))
    const custom = (widgetsConfig || [])
      .filter((w) => !standardIds.has(w.id) && w.enabled !== false)
      .map((w) => ({
        id: w.id,
        name: w.name || "Widget",
        icon: w.icon || "🧩",
        iconComp: LayoutGrid,
        desc: w.description || "Ferramenta rápida",
      }))
    return [...standard, ...custom]
  }, [widgetsConfig])

  const mobileAppsToRender = React.useMemo(() => {
    const activeApps = installedApps.filter((ia) => ia.status === "active")
    const activeAppMap = new Map(activeApps.map((ia) => [ia.app.id, ia]))
    const ordered: typeof activeApps = []

    finalMobileHomeOrder.forEach((key) => {
      if (key.startsWith("app:")) {
        const aid = key.replace("app:", "")
        const item = activeAppMap.get(aid)
        if (item) {
          ordered.push(item)
          activeAppMap.delete(aid)
        }
      }
    })

    activeAppMap.forEach((item) => {
      ordered.push(item)
    })

    return ordered
  }, [installedApps, finalMobileHomeOrder])

  return (
    <>
      {isMobile ? (
        /* ========================================================
           MOBILE LAYOUT (iOS / iPhone Style Launcher)
           ======================================================== */
        <div
          className="absolute left-0 right-0 top-0 bottom-0 flex flex-col bg-zinc-950 text-white overflow-hidden animate-in fade-in duration-300"
          style={mobileWallpaperStyle}
        >
          {/* Status Bar */}
          <div className="flex justify-between items-center px-5 pt-3 pb-1 text-[11px] font-bold tracking-tight opacity-90 select-none z-10">
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setShowMobileNotifications(true)
                  setShowMobileControlCenter(false)
                  setShowMobileAppSwitcher(false)
                }}
                className={`flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[9px] cursor-pointer active:scale-90 transition-all hover:bg-white/15 ${notifications.length > 0 ? "bg-blue-500/25 text-blue-300" : "bg-white/10 text-white/70"
                  }`}
              >
                <Bell className="size-3 text-blue-400 shrink-0" />
                <span className="tabular-nums font-mono font-extrabold">{notifications.length}</span>
              </button>

              {topBarConfig?.showClock !== false && (
                <>
                  <span className="opacity-25 text-white">|</span>
                  <span className="tabular-nums font-mono">
                    {currentTime ? currentTime.substring(0, 5) : "13:00"}
                  </span>
                </>
              )}

              {topBarConfig?.showWeather !== false && weatherInfo && (
                <>
                  <span className="opacity-25 text-white">|</span>
                  <div className="flex items-center gap-1 text-[9px] bg-white/10 px-2 py-0.5 rounded-md">
                    <CloudSun className="size-3 text-amber-400 shrink-0" />
                    <span>{weatherInfo.includes("°C") ? weatherInfo.split(",")[1]?.trim()?.replace("°C", "°") : "Clima"}</span>
                  </div>
                </>
              )}
            </div>

            <button
              onClick={() => {
                setShowMobileControlCenter(true)
                setShowMobileNotifications(false)
                setShowMobileAppSwitcher(false)
              }}
              className="flex items-center gap-1.5 text-[10px] font-extrabold tracking-wide uppercase text-blue-400 cursor-pointer active:scale-90 transition-all hover:text-blue-300 p-2 rounded-md hover:bg-white/10"
            >
              <Settings className="size-3.5 text-blue-400 shrink-0" />
            </button>
          </div>

          {/* Workspace Switcher Bar (Primeira linha sozinho) */}
          <div className="px-6 pt-2 pb-1 z-10 select-none">
            <DropdownMenu>
              <DropdownMenuTrigger
                className="w-full h-10 px-3.5 rounded-2xl border border-white/10 bg-white/10 dark:bg-black/35 text-white flex items-center justify-between hover:bg-white/15 backdrop-blur-md text-xs font-semibold shadow-sm cursor-pointer active:scale-[0.98] transition-all outline-none"
                title="Alternar Workspace"
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="size-6 rounded-lg bg-blue-600/80 text-white flex items-center justify-center font-extrabold text-[11px] shrink-0 shadow-xs">
                    {currentWorkspace?.name ? currentWorkspace.name.charAt(0).toUpperCase() : "W"}
                  </div>
                  <div className="flex flex-col items-start min-w-0 text-left">
                    <span className="text-[8.5px] uppercase tracking-wider text-white/50 font-bold leading-none">
                      Workspace
                    </span>
                    <span className="text-xs font-bold text-white truncate max-w-[210px] leading-tight mt-0.5">
                      {currentWorkspace?.name || "Principal"}
                    </span>
                  </div>
                </div>
                <ChevronsUpDown className="size-3.5 text-white/60 shrink-0" />
              </DropdownMenuTrigger>

              <DropdownMenuContent
                align="center"
                className="w-[calc(100vw-3rem)] max-w-sm bg-zinc-900/95 border-white/15 backdrop-blur-xl text-white p-1.5 rounded-2xl shadow-2xl z-[100] max-h-72 overflow-y-auto"
              >
                <DropdownMenuLabel className="text-[10px] uppercase font-bold text-white/50 px-2 py-1.5 flex items-center justify-between">
                  <span>Seus Workspaces</span>
                  {currentAccount?.name && (
                    <span className="text-[9px] font-normal text-white/40 truncate max-w-[120px]">{currentAccount.name}</span>
                  )}
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10 my-1" />

                {availableWorkspaces.length === 0 ? (
                  <div className="p-3 text-center text-xs text-white/40 italic">
                    Nenhum workspace disponível
                  </div>
                ) : (
                  availableWorkspaces.map((ws: any) => {
                    const isActive = currentWorkspace?.id === ws.id
                    return (
                      <DropdownMenuItem
                        key={ws.id}
                        onClick={() => {
                          if (handleSwitchWorkspace) {
                            handleSwitchWorkspace(ws)
                          } else if (setCurrentWorkspace) {
                            setCurrentWorkspace(ws)
                          }
                        }}
                        className={`flex items-center justify-between p-2.5 rounded-xl text-xs cursor-pointer transition-colors ${isActive
                          ? "bg-blue-500/20 text-blue-300 font-bold border border-blue-500/30"
                          : "hover:bg-white/10 focus:bg-white/10 text-white"
                          }`}
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div
                            className={`size-7 rounded-lg flex items-center justify-center font-bold text-xs shrink-0 ${isActive ? "bg-blue-600 text-white shadow-xs" : "bg-white/10 text-white/70"
                              }`}
                          >
                            {ws.name ? ws.name.charAt(0).toUpperCase() : "W"}
                          </div>
                          <div className="flex flex-col min-w-0 text-left">
                            <span className="font-semibold text-white truncate text-xs leading-tight">{ws.name}</span>
                            {ws.slug && (
                              <span className="text-[10px] text-white/40 font-mono truncate">{ws.slug}</span>
                            )}
                          </div>
                        </div>
                        {isActive && <Check className="size-4 text-blue-400 shrink-0 ml-2" />}
                      </DropdownMenuItem>
                    )
                  })
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Spotlight Search & Widgets Button */}
          <div className="px-6 pt-2 pb-2 z-10 flex items-center gap-3 select-none">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-white/50" />
              <input
                type="text"
                placeholder="Buscar aplicativos..."
                value={mobileSearch}
                onChange={(e) => setMobileSearch(e.target.value)}
                className="w-full rounded-2xl border border-white/10 bg-white/10 dark:bg-black/35 backdrop-blur-md pl-9 pr-4 py-2 text-xs text-white placeholder-white/50 focus:outline-none focus:ring-1.5 focus:ring-blue-500 font-sans"
              />
              {mobileSearch && (
                <button
                  onClick={() => setMobileSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white text-[10px] font-bold"
                >
                  Limpar
                </button>
              )}
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger
                className="h-8 px-3 rounded-xl border border-white/10 bg-white/10 dark:bg-black/35 text-white flex items-center gap-1.5 hover:bg-white/20 shrink-0 backdrop-blur-md text-xs font-semibold shadow-sm cursor-pointer active:scale-95 transition-all outline-none"
                title="Widgets & Ferramentas"
              >
                <LayoutGrid className="size-3.5 text-blue-400" />
                <span className="text-[11px]">Widgets</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-56 bg-zinc-900/95 border-white/15 backdrop-blur-xl text-white p-1 rounded-2xl shadow-2xl z-[100]"
              >
                <DropdownMenuLabel className="text-[10px] uppercase font-bold text-white/50 px-2 py-1.5">
                  Widgets & Ferramentas
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10 my-1" />
                {availableWidgets.map((w) => {
                  const IconComp = w.iconComp || LayoutGrid
                  return (
                    <DropdownMenuItem
                      key={w.id}
                      onClick={() => handleOpenWidgetAsApp(w.id, w.name, w.icon)}
                      className="flex items-center gap-2.5 p-2 rounded-xl text-xs cursor-pointer hover:bg-white/10 focus:bg-white/10 transition-colors"
                    >
                      <div className="size-7 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                        <IconComp className="size-4 text-blue-400" />
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="font-semibold text-white truncate">{w.name}</span>
                        <span className="text-[10px] text-white/50 truncate">{w.desc}</span>
                      </div>
                    </DropdownMenuItem>
                  )
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Main Launcher Grid */}
          <div className="flex-1 overflow-y-auto px-6 py-4 z-10 scrollbar-none pb-12">
            {mobileSearch ? (
              <div className="space-y-2">
                <h4 className="text-[10px] font-bold text-white/50 uppercase tracking-wider px-1">Resultados da Busca</h4>
                <div className="grid grid-cols-1 gap-2">
                  {mobileAppsToRender
                    .filter((ia) =>
                      (renamedAppNames[ia.app.id] || ia.app.name)
                        .toLowerCase()
                        .includes(mobileSearch.toLowerCase())
                    )
                    .map(({ id, app }) => {
                      const displayName = renamedAppNames[app.id] || app.name
                      return (
                        <button
                          key={id}
                          onClick={() => {
                            setMobileSearch("")
                            handleOpenApp(app)
                          }}
                          className="flex items-center gap-3 p-3 bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl text-left hover:bg-white/15 transition-all w-full cursor-pointer"
                        >
                          <AppIcon iconUrl={app.iconUrl} iframeUrl={app.iframeUrl} name={displayName} size="medium" />
                          <div className="flex flex-col min-w-0">
                            <span className="text-xs font-bold text-white truncate">{displayName}</span>
                            <span className="text-[9px] text-white/60">Aplicativo Instalado</span>
                          </div>
                        </button>
                      )
                    })}
                  {mobileAppsToRender.filter((ia) =>
                    (renamedAppNames[ia.app.id] || ia.app.name)
                      .toLowerCase()
                      .includes(mobileSearch.toLowerCase())
                  ).length === 0 && (
                      <div className="text-xs text-white/40 italic p-4 text-center">Nenhum aplicativo correspondente.</div>
                    )}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-x-3 gap-y-5 auto-rows-max">
                {mobileAppsToRender.map(({ id, app }) => {
                  const displayName = renamedAppNames[app.id] || app.name
                  return (
                    <div
                      key={id}
                      className="col-span-1 flex flex-col items-center relative select-none"
                    >
                      <button
                        onClick={() => handleOpenApp(app)}
                        className={`size-14 rounded-2xl flex items-center justify-center shadow-lg transition-transform duration-250 cursor-pointer ${theme === "light" ? "bg-white border border-zinc-200" : "bg-zinc-900 border border-zinc-200/25"
                          } active:scale-95 hover:scale-102`}
                        title={displayName}
                      >
                        <AppIcon iconUrl={app.iconUrl} iframeUrl={app.iframeUrl} name={displayName} size="medium" />
                      </button>
                      {desktopShowLabels !== false && (
                        <span className={`text-[9px] font-bold mt-1 text-center break-words w-full px-1 line-clamp-2 ${theme === "light" ? "text-zinc-850" : "text-white text-shadow-md"
                          }`}>
                          {displayName}
                        </span>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Home Indicator Bar */}
          <div
            onClick={() => setShowMobileAppSwitcher(true)}
            className="absolute bottom-1.5 left-0 right-0 flex justify-center z-25 cursor-pointer py-1.5 hover:opacity-80 active:scale-95"
          >
            <div className={`w-36 h-1.5 rounded-full shadow-sm ${theme === "light" ? "bg-zinc-800/60" : "bg-white/50"
              }`} />
          </div>
        </div>
      ) : (
        /* ========================================================
           PC / DESKTOP LAYOUT (Windows / Mac Style Workspace)
           ======================================================== */
        <div
          className="absolute left-0 right-0 flex flex-col bg-zinc-950 text-white overflow-hidden animate-in fade-in duration-300"
          style={{
            ...desktopWallpaperStyle,
            top: "100px",
            bottom: "0px"
          }}
          onContextMenu={handleDesktopContextMenu}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDesktopDrop}
        >
          {/* Desktop Background Click Catcher */}
          <div className="absolute inset-0 z-0" onClick={() => setContextMenu((prev: any) => ({ ...prev, visible: false }))} />

          {/* Workspace area of app icons and desktop grid widgets */}
          <div className="flex-1 overflow-hidden select-none z-10 relative h-full w-full">
            <div className="flex-1 relative h-full">
              {(() => {
                const activeApps = installedApps.filter(
                  (ia) => ia.status === "active" && !hiddenAppIds.includes(ia.app.id)
                )

                const isCompactScreen = windowSize.width < 1024
                const configuredCols = desktopGridCols || 12
                const maxCols = isCompactScreen ? Math.min(6, configuredCols) : configuredCols
                const maxRows = windowSize.height >= 1080 ? 6 : windowSize.height >= 850 ? 5 : 4
                const outerMargin = 12
                const cellW = (windowSize.width - 2 * outerMargin) / maxCols

                const gridOffsetY = 24
                const availH = Math.max(260, windowSize.height - 100 - 112 - gridOffsetY)
                const cellH = Math.floor(availH / maxRows)

                const gridOffsetX = outerMargin

                const occupied = new Set<string>()
                const resolvedPositions: Record<string, { col: number; row: number }> = {}

                const occupyRect = (col: number, row: number, colSpan: number, rowSpan: number) => {
                  for (let c = col; c < col + colSpan; c++) {
                    for (let r = row; r < row + rowSpan; r++) {
                      occupied.add(`${c},${r}`)
                    }
                  }
                }

                const isRectFree = (col: number, row: number, colSpan: number, rowSpan: number) => {
                  if (col + colSpan > maxCols || row + rowSpan > maxRows) return false
                  for (let c = col; c < col + colSpan; c++) {
                    for (let r = row; r < row + rowSpan; r++) {
                      if (occupied.has(`${c},${r}`)) return false
                    }
                  }
                  return true
                }

                const findFreeBlock = (startCol: number, startRow: number, colSpan: number, rowSpan: number) => {
                  let radius = 0
                  while (radius < 50) {
                    for (let dCol = -radius; dCol <= radius; dCol++) {
                      for (let dRow = -radius; dRow <= radius; dRow++) {
                        if (Math.abs(dCol) === radius || Math.abs(dRow) === radius) {
                          const col = Math.max(0, Math.min(maxCols - colSpan, startCol + dCol))
                          const row = Math.max(0, Math.min(maxRows - rowSpan, startRow + dRow))
                          if (isRectFree(col, row, colSpan, rowSpan)) {
                            return { col, row }
                          }
                        }
                      }
                    }
                    radius++
                  }
                  return { col: 0, row: 0 }
                }

                // PASS 1: Resolve Apps with Saved Positions (1x1)
                activeApps.forEach((ia) => {
                  const appId = ia.app.id
                  if (appPositions && appPositions[appId]) {
                    const savedPos = appPositions[appId]
                    let col = Math.min(Math.max(0, maxCols - 1), savedPos.col)
                    let row = Math.min(Math.max(0, maxRows - 1), savedPos.row)

                    if (!isRectFree(col, row, 1, 1)) {
                      const freeBlock = findFreeBlock(col, row, 1, 1)
                      col = freeBlock.col
                      row = freeBlock.row
                    }

                    resolvedPositions[appId] = { col, row }
                    occupyRect(col, row, 1, 1)
                  }
                })

                // PASS 2: Resolve Apps without Saved Positions
                activeApps.forEach((ia, idx) => {
                  const appId = ia.app.id
                  if (!resolvedPositions[appId]) {
                    const defaultCol = Math.floor(idx / maxRows) % maxCols
                    const defaultRow = idx % maxRows
                    const freeBlock = findFreeBlock(defaultCol, defaultRow, 1, 1)
                    resolvedPositions[appId] = freeBlock
                    occupyRect(freeBlock.col, freeBlock.row, 1, 1)
                  }
                })

                return (
                  <>
                    {/* Render Apps */}
                    {activeApps.map(({ id, app }) => {
                      const pos = resolvedPositions[app.id] || { col: 0, row: 0 }
                      const iconPx = 52
                      const itemWidth = 84
                      const itemHeight = 90
                      const cardOffset = Math.max(0, Math.floor((cellW - itemWidth) / 2))
                      const topOffset = Math.max(0, Math.floor((cellH - itemHeight) / 2))
                      const leftPos = gridOffsetX + pos.col * cellW + cardOffset
                      const topPos = gridOffsetY + pos.row * cellH + topOffset
                      return (
                        <ContextMenu key={id}>
                          <ContextMenuTrigger>
                            <div
                              draggable={true}
                              onDragStart={(e) => handleAppIconDragStart(e, app.id)}
                              onClick={() => handleOpenApp(app)}
                              style={{
                                position: 'absolute',
                                left: `${leftPos}px`,
                                top: `${topPos}px`,
                                width: `${itemWidth}px`,
                                height: `${itemHeight}px`
                              }}
                              className="flex flex-col items-center justify-center p-2 rounded-2xl hover:bg-white/15 dark:hover:bg-white/10 hover:backdrop-blur-md border border-transparent hover:border-white/15 transition-all duration-200 cursor-pointer group text-center select-none"
                            >
                              <div
                                className="flex items-center justify-center group-hover:scale-105 transition-all duration-200 shrink-0 mb-1"
                                style={{ width: `${iconPx}px`, height: `${iconPx}px` }}
                              >
                                <AppIcon iconUrl={app.iconUrl} iframeUrl={app.iframeUrl} name={app.name} size="medium" customSizePx={iconPx} />
                              </div>
                              {desktopShowLabels !== false && (
                                <span className="font-semibold text-center w-full px-1 leading-tight shrink-0 text-white text-shadow-md text-[11px] drop-shadow select-none truncate">
                                  {app.name}
                                </span>
                              )}
                            </div>
                          </ContextMenuTrigger>
                          <ContextMenuContent className="w-52 font-sans z-[9999]">
                            <ContextMenuItem onClick={() => handleOpenApp(app)} className="cursor-pointer text-xs py-1.5 flex items-center gap-1.5">
                              <span>🚀</span> Abrir Aplicativo
                            </ContextMenuItem>
                          </ContextMenuContent>
                        </ContextMenu>
                      )
                    })}
                  </>
                )
              })()}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
